import { camelCase } from 'es-toolkit';
import {
  Box,
  Divider,
  Form,
  FormField,
  Gleam,
  Heading,
  IconButton,
  Label,
  Popover,
  TextButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';
import { IconFilterOutline } from '@zillow/constellation-icons';
import { useContext, useEffect, useState } from 'react';
import ConditionalWrapper from '../ConditionalWrapper';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import { LeadManagementContext } from '../../contexts/LeadManagementContextProvider';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import { useAuth } from '../../AuthProvider';
import {
  emitFilterLeadsByMatchingCriteriaEvent,
  emitFilterLeadsByMessageTypeEvent,
  emitFilterLeadsByStatusInProcessEvent,
  FILTER_LEADS_BY_MATCHING_CRITERIA_EVENT_TYPE_ID,
  FILTER_LEADS_BY_MESSAGE_TYPE_EVENT_TYPE_ID,
  FILTER_LEADS_BY_STATUS_IN_PROCESS_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/leadManagementTable';

type FilterOption = {
  label: string;
  value: string;
};

type FilterSection = {
  label: string;
  type: string;
  filters: Array<FilterOption>;
};

type AppliedFilters = {
  messageType: string;
  screeningCriteria: string;
  status: string;
};

type HandleOnChangeArgs = {
  labelKey: string;
  type: string;
  value: string | Array<string>;
};

const FILTER_SECTIONS: Array<FilterSection> = [
  {
    label: 'Message type',
    type: 'folder',
    filters: [
      { label: 'Inbox', value: SatelliteConversationFolderTypeV2.INBOX },
      { label: 'Starred', value: SatelliteConversationFolderTypeV2.FAVORITE },
      { label: 'Unread', value: SatelliteConversationReadRepliedStatusTypeV2.UNREAD },
      { label: 'Archived', value: SatelliteConversationFolderTypeV2.ARCHIVED },
      { label: 'Reported', value: SatelliteConversationFolderTypeV2.SPAM },
    ],
  },
  {
    label: 'Screening criteria',
    type: 'minCriteriaMatches',
    filters: [
      { label: 'All', value: '0' },
      { label: 'Matches any criteria', value: '1' },
      { label: 'Matches 2+ criteria', value: '2' },
      { label: 'Matches all criteria', value: '3' },
    ],
  },
  {
    label: 'Status',
    type: 'renterProgressStatus',
    filters: [
      { label: 'All', value: SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL },
      { label: 'Inquiries', value: SatelliteConversationRenterProgressStatusTypeV2.INQUIRIES },
      { label: 'Tours', value: SatelliteConversationRenterProgressStatusTypeV2.TOURS },
      { label: 'Applications', value: SatelliteConversationRenterProgressStatusTypeV2.APPLICANTS },
    ],
  },
];

const filterChipValueToTriggerSourceName = new Map<string, string>([
  [SatelliteConversationFolderTypeV2.INBOX, 'inbox'],
  [SatelliteConversationFolderTypeV2.FAVORITE, 'starred'],
  [SatelliteConversationReadRepliedStatusTypeV2.UNREAD, 'unread'],
  [SatelliteConversationFolderTypeV2.ARCHIVED, 'archived'],
  [SatelliteConversationFolderTypeV2.SPAM, 'reported'],
  [SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL, 'all'],
  [SatelliteConversationRenterProgressStatusTypeV2.INQUIRIES, 'inquiries'],
  [SatelliteConversationRenterProgressStatusTypeV2.TOURS, 'tours'],
  [SatelliteConversationRenterProgressStatusTypeV2.APPLICANTS, 'applications'],
  ['1', 'any'],
  ['2', '2_plus'],
  ['3', 'all'],
]);

export default function LeadManagementFilters() {
  const { folder, listingAlias, minCriteriaMatches, renterProgressStatus, readRepliedStatus } =
    useContext(LeadManagementContext);
  const { removeAllQueryParams, updateQueryParams } = useQueryParamsNext();
  const { user } = useAuth();
  const [filterValues, setFilterValues] = useState<AppliedFilters>({
    messageType: readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.UNREAD ? readRepliedStatus : folder,
    screeningCriteria: minCriteriaMatches?.toString() || '0',
    status: renterProgressStatus || SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    setFilterValues({
      messageType:
        readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.UNREAD ? readRepliedStatus : folder,
      screeningCriteria: minCriteriaMatches?.toString() || '0',
      status: renterProgressStatus || SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
    });
  }, [folder, minCriteriaMatches, readRepliedStatus, renterProgressStatus]);

  const numberOfActiveFilters = Object.values(filterValues).filter(
    (value) =>
      value &&
      value !== SatelliteConversationFolderTypeV2.INBOX &&
      value !== SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL &&
      value !== '0',
  ).length;

  const getClickstreamInfoByLabelKey = (labelKey: string, value: string) => {
    const triggerSourceName = filterChipValueToTriggerSourceName.get(value);
    const triggerSourceNameLabelKeyExist = !!triggerSourceName && !!labelKey;

    let emitEvent = null,
      eventTypeId = null;
    if (triggerSourceNameLabelKeyExist) {
      if (labelKey === 'messageType') {
        emitEvent = () => emitFilterLeadsByMessageTypeEvent(user, { listingAlias }, triggerSourceName);
        eventTypeId = FILTER_LEADS_BY_MESSAGE_TYPE_EVENT_TYPE_ID;
      } else if (labelKey === 'screeningCriteria') {
        emitEvent = () => emitFilterLeadsByMatchingCriteriaEvent(user, { listingAlias }, triggerSourceName);
        eventTypeId = FILTER_LEADS_BY_MATCHING_CRITERIA_EVENT_TYPE_ID;
      } else if (labelKey === 'status') {
        emitEvent = () => emitFilterLeadsByStatusInProcessEvent(user, { listingAlias }, triggerSourceName);
        eventTypeId = FILTER_LEADS_BY_STATUS_IN_PROCESS_EVENT_TYPE_ID;
      }
    }

    return { emitEvent, eventTypeId };
  };

  const handleOnChange = ({ labelKey, type, value }: HandleOnChangeArgs) => {
    setIsDropdownOpen(true); // Prevents filter dropdown from auto closing after filter is applied
    setFilterValues({
      ...filterValues,
      [labelKey]: value,
    });

    const { emitEvent } = getClickstreamInfoByLabelKey(labelKey, value.toString());
    emitEvent?.();

    if (
      value === SatelliteConversationFolderTypeV2.INBOX ||
      value === SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL ||
      value === '0'
    ) {
      if (filterValues.messageType === SatelliteConversationReadRepliedStatusTypeV2.UNREAD) {
        return updateQueryParams({
          queryParamValues: {
            readRepliedStatus: null,
          },
        });
      }

      return updateQueryParams({
        queryParamValues: {
          [type]: null,
        },
      });
    }

    // Exception to applying type as the query param since "unread" is not a folder type
    // but is grouped with other folder types in the UI
    if (type === 'folder') {
      if (readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.UNREAD) {
        // Remove readRepliedStatus as a query param and set folder query param so that they both don't get set in url together
        return updateQueryParams({
          queryParamValues: {
            readRepliedStatus: null,
            folder: value,
          },
        });
      }

      if (value === SatelliteConversationReadRepliedStatusTypeV2.UNREAD) {
        // Apply readRepliedStatus as a query param and remove folder query param so that they both don't get set in url together
        return updateQueryParams({
          queryParamValues: {
            readRepliedStatus: SatelliteConversationReadRepliedStatusTypeV2.UNREAD,
            folder: null,
          },
        });
      }
    }

    return updateQueryParams({
      queryParamValues: {
        [type]: value,
      },
    });
  };

  const handleReset = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    setFilterValues({
      messageType: SatelliteConversationFolderTypeV2.INBOX,
      screeningCriteria: '0',
      status: SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
    });

    return removeAllQueryParams({
      listingAlias,
    });
  };

  const filterDropdownContent = (
    <Form>
      {FILTER_SECTIONS.map(({ filters, label, type }) => {
        const labelKey = camelCase(label);
        const value = filterValues[labelKey as keyof AppliedFilters];
        return (
          <FormField key={labelKey}>
            <Label>{label}</Label>
            <Box css={{ display: 'flex', marginTop: 'tighter' }}>
              <ToggleButtonGroup
                data-eventid={getClickstreamInfoByLabelKey(labelKey, value).eventTypeId}
                aria-label={`${label} filters`}
                onValueChange={(value) =>
                  handleOnChange({
                    labelKey,
                    type,
                    value,
                  })
                }
                value={value}
                conjoined={false}
                css={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'tighter',
                }}
              >
                {filters.map(({ label, value }) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    css={{ borderRadius: 'pill', paddingBlock: '2px', paddingInline: 'tighter' }}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </FormField>
        );
      })}
      <Divider />
      <TextButton
        disabled={numberOfActiveFilters === 0}
        onClick={handleReset}
        textStyle="body-sm"
        css={{ marginTop: 'default' }}
      >
        Reset
      </TextButton>
    </Form>
  );

  return (
    <ConditionalWrapper
      shouldRenderWrapperElement={numberOfActiveFilters > 0}
      wrapper={(children) => (
        <Gleam tone="info" count={numberOfActiveFilters}>
          {children}
        </Gleam>
      )}
    >
      <Popover
        header={<Heading level={4}>Filter by</Heading>}
        body={filterDropdownContent}
        trigger={<IconButton icon={<IconFilterOutline />} title="Filters" css={{ flexShrink: 0 }} />}
        onOpenChange={setIsDropdownOpen}
        open={isDropdownOpen}
        placement="bottom-start"
        css={{
          maxHeight: 'fit-content',
          '& div': {
            overflowY: 'visible',
          },
        }}
      />
    </ConditionalWrapper>
  );
}
